import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { Row, Col } from "react-bootstrap"

import { MtGroup, MtProjectGroup, MtUser } from "server/models"
import { deepCopy } from "client/utils"
import {
    StoreType,
    GroupFilters,
    ProjectGroupFilters,
    UserFilters,

    SortGroups,
    SortProjectGroups,
    SortUsers,

    setLifecyclePermissionsAccountGroupIds,
    setLifecyclePermissionsProjectGroupIds,
    setLifecyclePermissionsUserIds,
    setLifecyclePermissionsAccountGroupSort,
    setLifecyclePermissionsProjectGroupSort,
    setLifecyclePermissionsUserSort,
    setLifecyclePermissionsAccountGroupFilters,
    setLifecyclePermissionsAccountGroupSelected,
    setLifecyclePermissionsProjectGroupFilters,
    setLifecyclePermissionsProjectGroupSelected,
    setLifecyclePermissionsUserFilters,
    setLifecyclePermissionsUserSelected,
    updateLifecyclePermission,
    closeModal,
} from "client/store"
import {
    PermissionType,
    CommonPermissionItem,
} from "client/store/utils"
import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    PageMenu,
    PageMenuButton,
    Tab,
    TableSearchWrapper,
    Table,
    SortRows,
    PageForm,
    Button,
} from "client/components"

import AccountGroupSearch from "client/pages/search/groupSearch"
import ProjectGroupSearch from "client/pages/search/projectGroupSearch"
import UserSearch from "client/pages/search/userSearch"

import AccountGroupColumns from "./accountGroupColumns"
import ProjectGroupColumns from "./projectGroupColumns"
import UserColumns from "./userColumns"

const ACCOUNT_GROUPS = "accountGroups"
const PROJECT_GROUPS = "projectGroups"
const USERS = "users"

interface NotAssignedProps {
    section: string
    permissionId: string
    permissionType: PermissionType
    permissionItem: CommonPermissionItem
}

const NotAssigned = ({
    section,
    permissionId,
    permissionType,
    permissionItem,
}: NotAssignedProps) => {
    const accountGroupList = useSelector((state: StoreType) => state.group.groupList)
    const projectGroupList = useSelector((state: StoreType) => state.projectGroup.groupList)
    const userList = useSelector((state: StoreType) => state.user.userList)

    const accountUserList = useSelector((state: StoreType) => state.accountUser.accountUserList)
    const projectUserList = useSelector((state: StoreType) => state.projectUser.projectUserList)
    const project = useSelector((state: StoreType) => state.project.projectView)

    const accountGroupIds = useSelector((state: StoreType) => state.lifecyclePermissions.accountGroups.groupIds)
    const accountGroupSortColumn = useSelector((state: StoreType) => state.lifecyclePermissions.accountGroups.sortColumn)
    const accountGroupSortOrder = useSelector((state: StoreType) => state.lifecyclePermissions.accountGroups.sortOrder)
    const accountGroupFilters = useSelector((state: StoreType) => state.lifecyclePermissions.accountGroups.filters)
    const accountGroupSelected = useSelector((state: StoreType) => state.lifecyclePermissions.accountGroups.selected)

    const projectGroupIds = useSelector((state: StoreType) => state.lifecyclePermissions.projectGroups.groupIds)
    const projectGroupSortColumn = useSelector((state: StoreType) => state.lifecyclePermissions.projectGroups.sortColumn)
    const projectGroupSortOrder = useSelector((state: StoreType) => state.lifecyclePermissions.projectGroups.sortOrder)
    const projectGroupFilters = useSelector((state: StoreType) => state.lifecyclePermissions.projectGroups.filters)
    const projectGroupSelected = useSelector((state: StoreType) => state.lifecyclePermissions.projectGroups.selected)

    const userIds = useSelector((state: StoreType) => state.lifecyclePermissions.users.userIds)
    const userSortColumn = useSelector((state: StoreType) => state.lifecyclePermissions.users.sortColumn)
    const userSortOrder = useSelector((state: StoreType) => state.lifecyclePermissions.users.sortOrder)
    const userFilters = useSelector((state: StoreType) => state.lifecyclePermissions.users.filters)
    const userSelected = useSelector((state: StoreType) => state.lifecyclePermissions.users.selected)

    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [activeTab, setActiveTab] = useState(ACCOUNT_GROUPS)

    useEffect(() => {
        const sortedAccountGroupIds = SortGroups(
            Object.values(accountGroupList),
            accountGroupSortColumn,
            accountGroupSortOrder,
            accountGroupFilters,
        )
        const notAssignedAccountGroups = sortedAccountGroupIds.filter((id) => !permissionItem.accountGroups.includes(id))
        dispatch(setLifecyclePermissionsAccountGroupIds(notAssignedAccountGroups))
    }, [accountGroupList, accountGroupSortColumn, accountGroupSortOrder, accountGroupFilters])

    useEffect(() => {
        const sortedProjectGroupIds = SortProjectGroups(
            Object.values(projectGroupList),
            projectGroupSortColumn,
            projectGroupSortOrder,
            projectGroupFilters,
        )
        const notAssignedProjectGroups = sortedProjectGroupIds.filter((id) => !permissionItem.projectGroups.includes(id))
        dispatch(setLifecyclePermissionsProjectGroupIds(notAssignedProjectGroups))
    }, [projectGroupList, projectGroupSortColumn, projectGroupSortOrder, projectGroupFilters])

    useEffect(() => {
        const sortedUserIds = SortUsers(Object.values(userList), userSortColumn, userSortOrder, userFilters)
        let users: readonly number[]
        if (!project.id) {
            users = accountUserList[userData.accountId] || []
        } else {
            users = projectUserList[project.id] || []
        }
        const notAssignedUsers = sortedUserIds.filter((id) => users.includes(id) && !permissionItem.users.includes(id))
        dispatch(setLifecyclePermissionsUserIds(notAssignedUsers))
    }, [userList, userSortColumn, userSortOrder, userFilters])

    const onSortAccountGroups: SortRows = (column: keyof MtGroup, order) => {
        dispatch(setLifecyclePermissionsAccountGroupSort(column, order))
    }
    const onSortProjectGroups: SortRows = (column: keyof MtProjectGroup, order) => {
        dispatch(setLifecyclePermissionsProjectGroupSort(column, order))
    }
    const onSortUsers: SortRows = (column: keyof MtUser, order) => {
        dispatch(setLifecyclePermissionsUserSort(column, order))
    }

    const onAccountGroupFilter = (newFilters: GroupFilters) => {
        dispatch(setLifecyclePermissionsAccountGroupFilters(newFilters))
        dispatch(setLifecyclePermissionsAccountGroupSelected(null))
    }
    const onProjectGroupFilter = (newFilters: ProjectGroupFilters) => {
        dispatch(setLifecyclePermissionsProjectGroupFilters(newFilters))
        dispatch(setLifecyclePermissionsProjectGroupSelected(null))
    }
    const onUserFilter = (newFilters: UserFilters) => {
        dispatch(setLifecyclePermissionsUserFilters(newFilters))
        dispatch(setLifecyclePermissionsUserSelected(null))
    }

    const onSelect = () => {
        const newPermissionItem: CommonPermissionItem = deepCopy(permissionItem)
        const { accountGroups, projectGroups, users } = newPermissionItem
        newPermissionItem.accountGroups = accountGroups.concat(accountGroupSelected)
        newPermissionItem.projectGroups = projectGroups.concat(projectGroupSelected)
        newPermissionItem.users = users.concat(userSelected)

        dispatch(updateLifecyclePermission(
            section,
            permissionId,
            permissionType,
            newPermissionItem,
        ))

        dispatch(closeModal())
        dispatch(setLifecyclePermissionsAccountGroupSelected(null))
        dispatch(setLifecyclePermissionsProjectGroupSelected(null))
        dispatch(setLifecyclePermissionsUserSelected(null))
    }

    const accountGroupColumns = AccountGroupColumns()
    const projectGroupColumns = ProjectGroupColumns()
    const userColumns = UserColumns()

    let title = ""
    if (activeTab === ACCOUNT_GROUPS) title = lang.selectAccountGroup_s
    if (activeTab === PROJECT_GROUPS) title = lang.selectProjectGroup_s
    if (activeTab === USERS) title = lang.selectUser_s

    return (
        <Container>
            <PageHeader>
                <PageTitle>{title}</PageTitle>
            </PageHeader>
            <PageContent>
                <PageMenu>
                    <PageMenuButton
                        isActive={activeTab === ACCOUNT_GROUPS}
                        onClick={() => setActiveTab(ACCOUNT_GROUPS)}
                    >
                        {lang.accountGroups}
                    </PageMenuButton>
                    {
                        project.id && (
                            <PageMenuButton
                                isActive={activeTab === PROJECT_GROUPS}
                                onClick={() => setActiveTab(PROJECT_GROUPS)}
                            >
                                {lang.projectGroups}
                            </PageMenuButton>
                        )
                    }
                    <PageMenuButton
                        isActive={activeTab === USERS}
                        onClick={() => setActiveTab(USERS)}
                    >
                        {lang.users}
                    </PageMenuButton>
                </PageMenu>

                <Tab show={activeTab === ACCOUNT_GROUPS} noGrow>
                    <TableSearchWrapper>
                        <AccountGroupSearch
                            id="permissionAccountGroupList"
                            filters={accountGroupFilters}
                            onFilter={onAccountGroupFilter}
                        />
                    </TableSearchWrapper>
                    <Table
                        columns={accountGroupColumns}
                        dataList={accountGroupList}
                        dataIds={accountGroupIds}
                        selectedIds={accountGroupSelected}
                        sortColumn={accountGroupSortColumn}
                        sortOrder={accountGroupSortOrder}
                        rowSize="sm"
                        showPagination
                        onSort={onSortAccountGroups}
                    />
                </Tab>

                {
                    project.id && (
                        <Tab show={activeTab === PROJECT_GROUPS} noGrow>
                            <TableSearchWrapper>
                                <ProjectGroupSearch
                                    id="permissionProjectGroupList"
                                    filters={projectGroupFilters}
                                    onFilter={onProjectGroupFilter}
                                />
                            </TableSearchWrapper>
                            <Table
                                columns={projectGroupColumns}
                                dataList={projectGroupList}
                                dataIds={projectGroupIds}
                                selectedIds={projectGroupSelected}
                                sortColumn={projectGroupSortColumn}
                                sortOrder={projectGroupSortOrder}
                                rowSize="sm"
                                showPagination
                                onSort={onSortProjectGroups}
                            />
                        </Tab>
                    )
                }

                <Tab show={activeTab === USERS} noGrow>
                    <TableSearchWrapper>
                        <UserSearch
                            id="permissionUserList"
                            filters={userFilters}
                            onFilter={onUserFilter}
                        />
                    </TableSearchWrapper>
                    <Table
                        columns={userColumns}
                        dataList={userList}
                        dataIds={userIds}
                        selectedIds={userSelected}
                        sortColumn={userSortColumn}
                        sortOrder={userSortOrder}
                        rowSize="sm"
                        showPagination
                        onSort={onSortUsers}
                    />
                </Tab>

                <PageForm>
                    <Row>
                        <Col xs={12}>
                            <Button variant="success" row onClick={() => onSelect()}>
                                {lang.select}
                            </Button>
                        </Col>
                    </Row>
                </PageForm>
            </PageContent>
        </Container>
    )
}
export default NotAssigned
